import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Row,
  Col,
  Tooltip,
  Tabs,
  Card,
  message,
  Popconfirm,
  Typography,
  DatePicker,
  Tag,
  Alert,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShoppingCartOutlined,
  DownloadOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  DollarOutlined,
  TeamOutlined,
  InboxOutlined,
  LockOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { hasPermission, PERMISSIONS } from '../utils/permissions';
import { approvalsAPI, paymentsAPI } from '../api/config';
import { CLEAR_PO_PENDING_APPROVAL } from '../redux/constants/actionTypes';
import { fetchPendingApprovalsRequest, approvePORequest, rejectPOApprovalRequest } from '../redux/actions/approvalActions';
import { fetchHomeDataRequest } from '../redux/actions/homeActions';
import { fetchProducts } from '../redux/actions/productActions';
import {
  fetchOrdersRequest,
  createOrderRequest,
  redownloadOrderRequest,
  acceptShortfallRequest,
} from '../containers/PurchaseOrder/actions';
import './PurchaseOrder.css';

const { Option } = Select;

const PurchaseOrder = () => {
  const dispatch = useDispatch();
  
  // Get vendors, products, delivery addresses, and firm_names from Redux store
  const { vendors, products, delivery_addresses, firm_names, loading: homeLoading } = useSelector(state => state.home);

  // Products with vendor associations (fetched from /kp-products)
  const { products: productsWithVendors } = useSelector(state => state.products);

  // Get orders from Redux store
  const { orders, pagination, loading: ordersLoading, error, pendingApprovalPO, shortfallLoading } = useSelector(state => state.orders);
  const { actionLoading: approvalActionLoading } = useSelector(state => state.approvals);

  // Get permissions from Redux store
  const permissions = useSelector(state => state.auth.permissions || []);

  const prevVendorIdRef = useRef(null);

  // Check permissions
  const canViewGrid  = hasPermission(permissions, 'purchase_order/gridView');
  const canCreate    = hasPermission(permissions, 'purchase_order/create');
  const canApprove   = hasPermission(permissions, 'purchase_order/update');
  // Same capability as the excess-delivery amendment (delivered more than
  // ordered), just applied to closing out a short delivery instead.
  const canAmendQuantity = hasPermission(permissions, PERMISSIONS.PURCHASE_ORDER_AMEND);

  // Per-row approval/payment local cache (keyed by PO id)
  const [rowApprovals, setRowApprovals] = useState({});
  const [rowPayments, setRowPayments]   = useState({});
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError]   = useState('');
  const [rejectModalPO, setRejectModalPO] = useState(null);
  const [shortfallModal, setShortfallModal] = useState({ open: false, poId: null, item: null, reason: '', error: '' });

  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [productItems, setProductItems] = useState([{ id: Date.now() }]);
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [pendingApprovalAlert, setPendingApprovalAlert] = useState(null); // { poId, amount }
  const watchedItems = Form.useWatch('items', form) || [];
  const watchedVendorId = Form.useWatch('vendorId', form);
  const watchedDeliveryAddressId = Form.useWatch('deliveryAddress', form);
  const watchedDeliveryDate = Form.useWatch('deliveryDate', form);

  const orderDraftSummary = useMemo(() => {
    const validItems = (watchedItems || []).filter(item => item && item.product_id);
    const totalQuantity = validItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    const estimatedAmount = validItems.reduce(
      (sum, item) => sum + (Number(item.quantity || 0) * Number(item.rate || 0)),
      0
    );

    return {
      itemCount: validItems.length,
      totalQuantity,
      estimatedAmount,
    };
  }, [watchedItems]);

  const selectedVendorName = useMemo(() => {
    const vendor = vendors?.find(v => String(v.id) === String(watchedVendorId));
    return vendor ? (vendor.firm_name || vendor.name) : 'Not selected';
  }, [vendors, watchedVendorId]);

  const selectedDeliveryPoint = useMemo(() => {
    const address = delivery_addresses?.find(addr => String(addr.id) === String(watchedDeliveryAddressId));
    if (!address) return 'Not selected';
    return address.unit_name || `${address.address}, ${address.city}`;
  }, [delivery_addresses, watchedDeliveryAddressId]);

  // Products filtered by selected vendor (only show products linked to that vendor)
  const vendorFilteredProducts = useMemo(() => {
    if (!watchedVendorId) return [];
    return (productsWithVendors || []).filter(p =>
      p.vendors && p.vendors.some(v => String(v.id) === String(watchedVendorId))
    );
  }, [watchedVendorId, productsWithVendors]);

  const disablePastDates = (current) => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    return current && current.valueOf() < startOfToday.getTime();
  };

  // Fetch home data, products (with vendor associations), and orders on component mount
  useEffect(() => {
    dispatch(fetchHomeDataRequest());
    dispatch(fetchProducts());
    if (canViewGrid) {
      dispatch(fetchOrdersRequest(currentPage, 10));
    }
  }, [dispatch, currentPage, canViewGrid]);

  // Clear product rows when vendor selection changes
  useEffect(() => {
    if (prevVendorIdRef.current !== null && prevVendorIdRef.current !== watchedVendorId) {
      setProductItems([{ id: Date.now() }]);
      form.setFieldsValue({ items: [] });
    }
    prevVendorIdRef.current = watchedVendorId;
  }, [watchedVendorId, form]);

  // Handle product selection to auto-fill rate and product name
  const handleProductChange = (productId, index) => {
    const selectedProduct = products.find(p => String(p.id) === String(productId));
    
    if (selectedProduct) {
      const rate = selectedProduct.purchase_rate ||
                   selectedProduct.purchaseRate ||
                   selectedProduct.rate ||
                   selectedProduct.price ||
                   0;
      const productName = selectedProduct.product_name || selectedProduct.name || '';
      
      // Get current form values
      const currentValues = form.getFieldsValue();
      const items = currentValues.items || [];
      
      // Update the specific item
      items[index] = {
        ...items[index],
        product_name: productName,
        rate: rate
      };
      
      form.setFieldsValue({ items });
    }
  };

  // Add new product item
  const handleAddProduct = () => {
    setProductItems([...productItems, { id: Date.now() }]);
  };

  // Remove product item
  const handleRemoveProduct = (index) => {
    if (productItems.length > 1) {
      const newItems = productItems.filter((_, i) => i !== index);
      setProductItems(newItems);
      
      // Update form values
      const currentValues = form.getFieldsValue();
      const items = currentValues.items || [];
      items.splice(index, 1);
      form.setFieldsValue({ items });
    } else {
      message.warning('At least one product is required');
    }
  };

  const handleAddNew = () => {
    form.resetFields();
    setProductItems([{ id: Date.now() }]);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    form.resetFields();
    setProductItems([{ id: Date.now() }]);
  };

  const handleRefresh = () => {
    dispatch(fetchOrdersRequest(currentPage, 10));
  };

  const handleSubmit = async (values) => {
    try {
      const selectedVendor = vendors.find(v => String(v.id) === String(values.vendorId));
      const selectedAddress = delivery_addresses.find(addr => String(addr.id) === String(values.deliveryAddress));
      
      // Format delivery address string
      const deliveryAddressString = selectedAddress
        ? `${selectedAddress.address}, ${selectedAddress.city}, ${selectedAddress.state}, ${selectedAddress.pincode}`
        : '';
      
      // Transform items array to match API format
      const items = (values.items || []).map(item => {
        const selectedProduct = products.find(p => String(p.id) === String(item.product_id));
        return {
          product_id: parseInt(item.product_id),
          product_name: selectedProduct?.product_name || selectedProduct?.name || item.product_name || '',
          quantity: parseFloat(item.quantity),
          rate: parseFloat(item.rate)
        };
      });
      
      // Validate at least one item
      if (!items || items.length === 0) {
        message.error('Please add at least one product');
        return;
      }
      
      const payload = {
        vendor_id: parseInt(values.vendorId),
        vendor_name: selectedVendor?.firm_name || selectedVendor?.name || '',
        delivery_address: deliveryAddressString,
        delivery_address_id: parseInt(values.deliveryAddress),
        firm_name: values.firmName,
        delivery_date: values.deliveryDate ? values.deliveryDate.format('YYYY-MM-DD') : null,
        items: items
      };
      
      // Show loading message
      message.loading('Creating purchase order and generating PDF...', 2.5);
      
      dispatch(createOrderRequest(payload));
      handleModalClose();
      
      // The success message will be shown by the saga after PDF download
      // Refresh the orders list
      setTimeout(() => {
        dispatch(fetchOrdersRequest(currentPage, 10));
      }, 500);
    } catch (error) {
      message.error('Failed to create purchase order');
    }
  };


  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
  };

  // Handle redownload order using backend API
  const handleRedownload = (orderId) => {
    dispatch(redownloadOrderRequest(orderId));
  };

  // Helper function to get delivery address display text
  const getDeliveryAddressText = (addressId) => {
    if (!delivery_addresses || !addressId) return addressId || 'N/A';
    
    const address = delivery_addresses.find(addr => addr.id === parseInt(addressId));
    if (address) {
      return `${address.address}, ${address.city}, ${address.state}, ${address.pincode}, ${address.country}`;
    }
    return addressId;
  };

  // Fetch approval + payment details when a row expands
  const handleRowExpand = async (expanded, record) => {
    if (!expanded) return;
    // Fetch approval status (only for pending_approval POs or if not yet loaded)
    if (!rowApprovals[record.id]) {
      try {
        const res = await approvalsAPI.getPOApprovalStatus(record.id);
        setRowApprovals(prev => ({ ...prev, [record.id]: res.data }));
      } catch {
        // 404 = no approval request for this PO (below threshold) — that's fine
      }
    }
    // Fetch payments
    if (!rowPayments[record.id]) {
      try {
        const res = await paymentsAPI.getPOPayments(record.id);
        setRowPayments(prev => ({ ...prev, [record.id]: res.data || [] }));
      } catch {
        // no payments yet — ignore
      }
    }
  };

  const handleApprovePO = (poId) => {
    dispatch(approvePORequest(poId, { remarks: '' }));
    // Refresh local approval state after a short delay
    setTimeout(async () => {
      try {
        const res = await approvalsAPI.getPOApprovalStatus(poId);
        setRowApprovals(prev => ({ ...prev, [poId]: res.data }));
        dispatch(fetchOrdersRequest(currentPage, 10));
        dispatch(fetchPendingApprovalsRequest());
      } catch {}
    }, 800);
  };

  const handleRejectPO = () => {
    if (!rejectReason.trim()) { setRejectError('Rejection reason is required.'); return; }
    const poId = rejectModalPO;
    dispatch(rejectPOApprovalRequest(poId, { rejection_reason: rejectReason }));
    setRejectModalPO(null);
    setRejectReason('');
    setRejectError('');
    setTimeout(async () => {
      try {
        const res = await approvalsAPI.getPOApprovalStatus(poId);
        setRowApprovals(prev => ({ ...prev, [poId]: res.data }));
        dispatch(fetchOrdersRequest(currentPage, 10));
        dispatch(fetchPendingApprovalsRequest());
      } catch {}
    }, 800);
  };

  const openShortfallModal = (poId, item) => setShortfallModal({ open: true, poId, item, reason: '', error: '' });
  const closeShortfallModal = () => setShortfallModal({ open: false, poId: null, item: null, reason: '', error: '' });

  const handleAcceptShortfall = () => {
    if (!shortfallModal.reason.trim()) {
      setShortfallModal(prev => ({ ...prev, error: 'A reason is required to accept the shortfall.' }));
      return;
    }
    dispatch(acceptShortfallRequest(shortfallModal.poId, shortfallModal.item.id, shortfallModal.reason.trim(), currentPage));
    closeShortfallModal();
  };

  // Expandable row renderer to show order details
  const expandedRowRender = (record) => {
    const approval = rowApprovals[record.id];
    const payments = rowPayments[record.id] || [];
    const totalPaid = payments.filter(p => p.status === 'completed').reduce((s, p) => s + (p.amount || 0), 0);
    const itemColumns = [
      {
        title: 'Product Name',
        dataIndex: 'product_name',
        key: 'product_name',
        width: '20%',
        render: (text) => <strong style={{ fontSize: '13px' }}>{text}</strong>,
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
        key: 'quantity',
        width: '9%',
        align: 'center',
        render: (text, item) => (
          <span style={{ fontSize: '12px', fontWeight: '500' }}>
            {text} {item.unit ? `${item.unit}` : ''}
          </span>
        ),
      },
      {
        title: 'Pending',
        key: 'pending_quantity',
        width: '9%',
        align: 'center',
        render: (_, item) => {
          const pending = Number(item.pending_quantity ?? (item.quantity - (item.delivered_quantity || 0)));
          return pending > 0.0005
            ? <span style={{ fontSize: '12px', fontWeight: '500', color: '#fa8c16' }}>{pending.toFixed(3)} {item.unit || ''}</span>
            : <span style={{ fontSize: '12px', color: '#8c8c8c' }}>-</span>;
        },
      },
      {
        title: 'Action',
        key: 'shortfall_action',
        width: '13%',
        align: 'center',
        render: (_, item) => {
          const pending = Number(item.pending_quantity ?? (item.quantity - (item.delivered_quantity || 0)));
          if (pending <= 0.0005) return null;
          if (!canAmendQuantity) {
            return (
              <Tooltip title="You do not have permission to amend purchase order quantities.">
                <Button size="small" disabled>Close &amp; Accept Shortfall</Button>
              </Tooltip>
            );
          }
          return (
            <Button size="small" onClick={() => openShortfallModal(record.id, item)}>
              Close &amp; Accept Shortfall
            </Button>
          );
        },
      },
      {
        title: 'Rate',
        dataIndex: 'rate',
        key: 'rate',
        width: '11%',
        align: 'right',
        render: (text) => <span style={{ fontSize: '12px' }}>₹{parseFloat(text || 0).toFixed(2)}</span>,
      },
      {
        title: 'Discount',
        key: 'discount',
        width: '12%',
        align: 'right',
        render: (_, item) => (
          <div style={{ fontSize: '11px', color: '#52c41a' }}>
            {item.discount ? (
              <>
                <div style={{ fontWeight: '600' }}>₹{parseFloat(item.discount_amount || 0).toFixed(2)}</div>
                <div style={{ fontSize: '10px', color: '#8c8c8c' }}>({item.discount}%)</div>
              </>
            ) : '-'}
          </div>
        ),
      },
      {
        title: 'Tax',
        key: 'tax',
        width: '12%',
        align: 'right',
        render: (_, item) => (
          <div style={{ fontSize: '11px', color: '#fa8c16' }}>
            {item.tax_slab ? (
              <>
                <div style={{ fontWeight: '600' }}>₹{parseFloat(item.tax_amount || 0).toFixed(2)}</div>
                <div style={{ fontSize: '10px', color: '#8c8c8c' }}>({item.tax_slab}%)</div>
              </>
            ) : '-'}
          </div>
        ),
      },
      {
        title: 'Final Amount',
        dataIndex: 'amount',
        key: 'amount',
        width: '14%',
        align: 'right',
        render: (text) => (
          <strong style={{ fontSize: '13px', color: '#1890ff' }}>
            ₹{parseFloat(text || 0).toFixed(2)}
          </strong>
        ),
      },
    ];

    return (
      <div style={{ padding: '12px 24px', background: '#fafafa' }}>
        <Table
          columns={itemColumns}
          dataSource={record.items || []}
          pagination={false}
          size="small"
          rowKey="id"
          className="items-detail-table"
        />

        {/* ── Approval Section ─────────────────────────────────────── */}
        {(record.order_status === 'pending_approval' || approval) && (
          <div style={{ marginTop: 10 }}>
            {!approval && record.order_status === 'pending_approval' && (
              <Alert type="warning" showIcon icon={<ClockCircleOutlined />}
                message="This PO is awaiting approval before it can proceed." />
            )}
            {approval && approval.status === 'pending_approval' && (
              <Alert type="warning" showIcon icon={<ClockCircleOutlined />}
                message={`Pending Approval — Requested by ${approval.requested_by_name || '—'}`}
                action={canApprove ? (
                  <Space>
                    <Popconfirm title="Approve this PO?" onConfirm={() => handleApprovePO(record.id)} okText="Approve">
                      <Button size="small" type="primary" icon={<CheckCircleOutlined />} loading={approvalActionLoading}>Approve</Button>
                    </Popconfirm>
                    <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => setRejectModalPO(record.id)}>Reject</Button>
                  </Space>
                ) : null}
              />
            )}
            {approval && approval.status === 'approved' && (
              <Alert type="success" showIcon icon={<CheckCircleOutlined />}
                message={`Approved by ${approval.approved_by_name || '—'}${approval.remarks ? ` — "${approval.remarks}"` : ''}`} />
            )}
            {approval && approval.status === 'rejected' && (
              <Alert type="error" showIcon icon={<CloseCircleOutlined />}
                message={`Rejected: ${approval.rejection_reason || '—'} (by ${approval.rejected_by_name || '—'})`} />
            )}
          </div>
        )}

        {/* ── Payments summary ─────────────────────────────────────── */}
        {payments.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Tag color="blue" icon={<CreditCardOutlined />}> {payments.length} payment(s)</Tag>
            <Tag color="green">Paid: ₹{Number(totalPaid).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</Tag>
          </div>
        )}

        {/* Order Summary */}
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e8e8e8'
        }}>
          <Row gutter={[16, 8]} style={{ fontSize: '13px' }}>
            <Col span={12}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>Subtotal:</span>
                  <span style={{ fontWeight: '500' }}>₹{parseFloat(record.subtotal || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>Total Discount:</span>
                  <span style={{ fontWeight: '500', color: '#52c41a' }}>-₹{parseFloat(record.total_discount || 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>Total Tax:</span>
                  <span style={{ fontWeight: '500', color: '#fa8c16' }}>+₹{parseFloat(record.total_tax || 0).toFixed(2)}</span>
                </div>
              </Space>
            </Col>
            <Col span={12}>
              <div style={{
                padding: '8px 12px',
                background: '#e6f7ff',
                borderRadius: '4px',
                border: '1px solid #91d5ff'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#0050b3' }}>Grand Total:</span>
                  <span style={{ fontSize: '16px', fontWeight: '700', color: '#0050b3' }}>
                    ₹{parseFloat(record.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  // Table columns definition with responsive widths
  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      fixed: 'left',
      render: (id) => <span style={{ fontWeight: 600, color: '#8c8c8c', fontSize: 12 }}>#{id}</span>,
    },
    {
      title: 'Firm / Vendor',
      key: 'firm_vendor',
      width: 160,
      ellipsis: true,
      render: (_, record) => (
        <div style={{ lineHeight: 1.4 }}>
          <Tooltip title={record.firm_name}>
            <div style={{ fontWeight: 600, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.firm_name}</div>
          </Tooltip>
          <Tooltip title={record.vendor_name}>
            <div style={{ color: '#595959', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.vendor_name}</div>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Products',
      dataIndex: 'items',
      key: 'items',
      width: 130,
      ellipsis: true,
      render: (items) => {
        if (!items || items.length === 0) return '—';
        const itemsText = items.map(item => `${item.product_name} (${item.quantity})`).join(', ');
        return (
          <Tooltip placement="topLeft" title={itemsText}>
            <span style={{ fontSize: '12px' }}>
              {items.length === 1
                ? (items[0].product_name || '').substring(0, 18)
                : `${items.length} items`}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 110,
      align: 'right',
      render: (total_amount) => (
        <strong style={{ fontSize: '13px', color: '#0050b3' }}>
          ₹{Number(total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </strong>
      ),
    },
    {
      title: 'Dates',
      key: 'dates',
      width: 100,
      render: (_, record) => (
        <div style={{ lineHeight: 1.5, fontSize: 11 }}>
          <div style={{ color: '#8c8c8c' }}>
            {record.created_at ? new Date(record.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
          </div>
          {record.delivery_date && (
            <div style={{ color: '#52c41a' }}>
              ↗ {new Date(record.delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Order Status',
      dataIndex: 'order_status',
      key: 'order_status',
      width: 120,
      render: (status) => {
        const statusConfig = {
          pending_approval: { color: 'gold', text: 'Pending Approval' },
          open: { color: 'default', text: 'Open' },
          in_delivery: { color: 'processing', text: 'In Delivery' },
          delivered: { color: 'cyan', text: 'Delivered' },
          billed: { color: 'orange', text: 'Billed' },
          verified: { color: 'purple', text: 'Verified' },
          closed: { color: 'success', text: 'Closed' },
          cancelled: { color: 'error', text: 'Cancelled' },
        };
        const config = statusConfig[status] || { color: 'default', text: status || 'Open' };
        return <Tag color={config.color} style={{ fontSize: '10px', fontWeight: '600' }}>{config.text}</Tag>;
      },
    },
    {
      title: 'Delivery / Bill',
      key: 'del_bill_status',
      width: 120,
      render: (_, record) => {
        const deliveryConfig = {
          pending: { color: 'orange', text: 'Pending' },
          in_transit: { color: 'blue', text: 'In Transit' },
          partially_delivered: { color: 'purple', text: 'Partial' },
          delivered: { color: 'green', text: 'Delivered' },
          cancelled: { color: 'red', text: 'Cancelled' },
        };
        const billConfig = {
          pending: { color: 'default', text: 'Bill Pending' },
          partially_received: { color: 'orange', text: 'Part. Billed' },
          fully_received: { color: 'blue', text: 'Fully Billed' },
          verified: { color: 'purple', text: 'Verified' },
          closed: { color: 'green', text: 'Closed' },
        };
        const dc = deliveryConfig[record.delivery_status] || { color: 'default', text: record.delivery_status || '—' };
        const bc = billConfig[record.bill_status] || { color: 'default', text: record.bill_status || '—' };
        return (
          <div>
            <Tag color={dc.color} style={{ fontSize: '10px', fontWeight: '600', marginBottom: 2 }}>{dc.text}</Tag>
            <Tag color={bc.color} style={{ fontSize: '10px', fontWeight: '600' }}>{bc.text}</Tag>
          </div>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 75,
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Download PDF">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="small"
            onClick={() => handleRedownload(record.id)}
          >
            PDF
          </Button>
        </Tooltip>
      ),
    }
  ];

  const loading = homeLoading || ordersLoading;

  // After PO creation, check if the newest order needs approval
  useEffect(() => {
    if (!orders || orders.length === 0) return;
    const newest = orders[0];
    if (newest && newest.order_status === 'pending_approval') {
      setPendingApprovalAlert({ poId: newest.id, amount: newest.total_amount, vendor: newest.vendor_name });
    }
  }, [orders]);

  // Filter orders based on order status
  const filteredOrders = orderStatusFilter === 'all'
    ? orders
    : orders.filter(order => order.order_status === orderStatusFilter);

  // If user doesn't have permission to view grid, show message
  if (!canViewGrid) {
    return (
      <div className="purchase-order">
        <div className="purchase-order-container">
          <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
            You don't have permission to view purchase orders.
          </div>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'orders',
      label: (
        <span>
          <ShoppingCartOutlined />
          Purchase Orders
        </span>
      ),
      children: (
        <div className="purchase-order-content">
          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon"><ShoppingCartOutlined /></div>
              <div className="stat-content">
                <div className="stat-value">{filteredOrders.length}</div>
                <div className="stat-label">Total Orders</div>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#28a745' }}>
              <div className="stat-icon" style={{ background: '#d4edda', color: '#155724' }}><DollarOutlined /></div>
              <div className="stat-content">
                <div className="stat-value">
                  ₹{filteredOrders.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0).toLocaleString('en-IN')}
                </div>
                <div className="stat-label">Total Value</div>
              </div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#17a2b8' }}>
              <div className="stat-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}><TeamOutlined /></div>
              <div className="stat-content">
                <div className="stat-value">{new Set(filteredOrders.map(o => o.vendor_id)).size}</div>
                <div className="stat-label">Unique Vendors</div>
              </div>
            </div>
          </div>

          {/* Pending Approval Banner */}
          {pendingApprovalAlert && (
            <Alert
              type="warning"
              showIcon
              closable
              onClose={() => setPendingApprovalAlert(null)}
              message="Purchase Order Requires Approval"
              description={
                <span>
                  PO #{pendingApprovalAlert.poId} for{' '}
                  <strong>{pendingApprovalAlert.vendor}</strong> (₹{Number(pendingApprovalAlert.amount).toLocaleString('en-IN')})
                  {' '}exceeds the approval threshold. It is currently{' '}
                  <strong>Pending Approval</strong> and cannot proceed until a manager approves it.
                </span>
              }
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Order Status Distribution — compact inline strip */}
          <div style={{ background: '#fff', borderRadius: 8, padding: '7px 14px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,.05)', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#666', marginRight: 4 }}>Status:</span>
            {orders.filter(o => o.order_status === 'pending_approval').length > 0 && (
              <Tag color="gold" style={{ margin: 0 }}>⏳ Pending: {orders.filter(o => o.order_status === 'pending_approval').length}</Tag>
            )}
            <Tag color="default" style={{ margin: 0 }}>Open: {orders.filter(o => o.order_status === 'open').length}</Tag>
            <Tag color="processing" style={{ margin: 0 }}>In Delivery: {orders.filter(o => o.order_status === 'in_delivery').length}</Tag>
            <Tag color="cyan" style={{ margin: 0 }}>Delivered: {orders.filter(o => o.order_status === 'delivered').length}</Tag>
            <Tag color="orange" style={{ margin: 0 }}>Billed: {orders.filter(o => o.order_status === 'billed').length}</Tag>
            <Tag color="purple" style={{ margin: 0 }}>Verified: {orders.filter(o => o.order_status === 'verified').length}</Tag>
            <Tag color="success" style={{ margin: 0 }}>Closed: {orders.filter(o => o.order_status === 'closed').length}</Tag>
            <Tag color="error" style={{ margin: 0 }}>Cancelled: {orders.filter(o => o.order_status === 'cancelled').length}</Tag>
          </div>

          {/* Table Header */}
          <div className="modern-table-container">
            <div className="table-header">
              <h2 className="table-title">All Purchase Orders</h2>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <Select
                  value={orderStatusFilter}
                  onChange={setOrderStatusFilter}
                  style={{ width: 150 }}
                  size="large"
                >
                  <Option value="all">All Status</Option>
                  <Option value="pending_approval">⏳ Pending Approval</Option>
                  <Option value="open">Open</Option>
                  <Option value="in_delivery">In Delivery</Option>
                  <Option value="delivered">Delivered</Option>
                  <Option value="billed">Billed</Option>
                  <Option value="verified">Verified</Option>
                  <Option value="closed">Closed</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
                <button
                  className="btn btn-secondary"
                  onClick={handleRefresh}
                  title="Refresh"
                >
                  <ReloadOutlined /> Refresh
                </button>
                {canCreate && (
                  <button
                    className="btn btn-primary"
                    onClick={handleAddNew}
                  >
                    <PlusOutlined /> Create New Order
                  </button>
                )}
              </div>
            </div>
            <Table
              columns={columns}
              dataSource={filteredOrders}
              rowKey="id"
              loading={loading}
              className="purchase-order-table"
              expandable={{
                expandedRowRender,
                onExpand: handleRowExpand,
                expandRowByClick: false,
                expandIcon: ({ expanded, onExpand, record }) => (
                  expanded ? (
                    <Tooltip title="Collapse details">
                      <Button
                        type="text"
                        size="small"
                        icon={<MinusCircleOutlined style={{ color: '#1890ff' }} />}
                        onClick={(e) => onExpand(record, e)}
                        style={{ padding: '0 4px' }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="View details">
                      <Button
                        type="text"
                        size="small"
                        icon={<PlusOutlined style={{ color: '#52c41a' }} />}
                        onClick={(e) => onExpand(record, e)}
                        style={{ padding: '0 4px' }}
                      />
                    </Tooltip>
                  )
                ),
              }}
              pagination={{
                current: currentPage,
                total: filteredOrders.length,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} orders`,
                onChange: handlePageChange,
              }}
              size="small"
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: (
                  <div className="empty-state">
                    <div className="empty-state-icon"><InboxOutlined /></div>
                    <div className="empty-state-text">
                      {orderStatusFilter === 'all'
                        ? 'No purchase orders found'
                        : `No ${orderStatusFilter} orders found`
                      }
                    </div>
                    {canCreate && orderStatusFilter === 'all' && (
                      <button
                        className="btn btn-primary"
                        onClick={handleAddNew}
                      >
                        Create Your First Order
                      </button>
                    )}
                  </div>
                ),
              }}
            />
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="page-container">
      <div className="page-content">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-title">
            <div className="page-header-icon"><ShoppingCartOutlined /></div>
            <div className="page-header-text">
              <h1>Purchase Order Management</h1>
              <p>Create and manage purchase orders for your business</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="card">
          <Tabs
            type="card"
            size="large"
            defaultActiveKey="orders"
            items={tabItems}
            tabPosition="top"
          />
        </div>
      </div>

      <Modal
        title={
          <div className="po-modal-title">
            <span className="po-modal-title-icon">
              <ShoppingCartOutlined />
            </span>
            <div>
              <div className="po-modal-heading">Create New Purchase Order</div>
              <div className="po-modal-subheading">
                Fill order details and line items to generate and download a polished PO PDF.
              </div>
            </div>
          </div>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={900}
        destroyOnClose
        className="purchase-order-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          className="purchase-order-form professional-form"
        >
          <div className="po-quick-summary">
            <div className="po-quick-summary-item">
              <span className="po-summary-label">Vendor</span>
              <span className="po-summary-value">{selectedVendorName}</span>
            </div>
            <div className="po-quick-summary-item">
              <span className="po-summary-label">Delivery Point</span>
              <span className="po-summary-value">{selectedDeliveryPoint}</span>
            </div>
            <div className="po-quick-summary-item">
              <span className="po-summary-label">Items Selected</span>
              <span className="po-summary-value">{orderDraftSummary.itemCount}</span>
            </div>
            <div className="po-quick-summary-item">
              <span className="po-summary-label">Estimated Total</span>
              <span className="po-summary-value highlight">
                ₹{orderDraftSummary.estimatedAmount.toLocaleString('en-IN', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            </div>
          </div>

          <div className="form-section">
            <Typography.Title level={5} className="section-title">
              Order Information
            </Typography.Title>
            <Typography.Text className="section-description">
              Select billing firm, vendor, and delivery details to start the order.
            </Typography.Text>
            <Row gutter={[20, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="Bill to (Firm Name)"
                  name="firmName"
                  rules={[
                    {
                      required: true,
                      message: 'Please select the firm name!',
                    },
                  ]}
                >
                  <Select
                    placeholder="Select billing firm"
                    showSearch
                    size="large"
                    filterOption={(input, option) =>
                      String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {firm_names && firm_names.length > 0 ? (
                      firm_names.map(firm => (
                        <Option key={firm.id} value={firm.firm_name}>
                          {firm.firm_name}
                        </Option>
                      ))
                    ) : (
                      <Option disabled>No firm names available</Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label="Bill From (Vendor)"
                  name="vendorId"
                  rules={[
                    {
                      required: true,
                      message: 'Please select a vendor!',
                    },
                  ]}
                >
                  <Select
                    placeholder="Select vendor"
                    showSearch
                    size="large"
                    filterOption={(input, option) =>
                      String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {vendors && vendors.length > 0 ? (
                      vendors.map(vendor => (
                        <Option key={vendor.id} value={vendor.id}>
                          {vendor.firm_name || vendor.name}
                        </Option>
                      ))
                    ) : (
                      <Option disabled>No vendors available</Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[20, 0]}>
              <Col xs={24} sm={16}>
                <Form.Item
                  label="Delivery Address"
                  name="deliveryAddress"
                  rules={[
                    {
                      required: true,
                      message: 'Please select delivery address!',
                    },
                  ]}
                >
                  <Select
                    placeholder="Select delivery address"
                    showSearch
                    size="large"
                  >
                    {delivery_addresses && delivery_addresses.length > 0 ? (
                      delivery_addresses.map(address => (
                        <Option key={address.id} value={address.id}>
                          {address.unit_name || `${address.address}, ${address.city}`}
                        </Option>
                      ))
                    ) : (
                      <Option disabled>No delivery addresses available</Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item
                  label="Delivery Date"
                  name="deliveryDate"
                  rules={[
                    {
                      required: true,
                      message: 'Please select delivery date!',
                    },
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    size="large"
                    format="DD/MM/YYYY"
                    placeholder="Select delivery date"
                    disabledDate={disablePastDates}
                    allowClear={false}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="form-section products-section">
            <div className="products-section-header">
              <div>
                <Typography.Title level={5} className="section-title">
                  Product Details
                </Typography.Title>
                <Typography.Text className="section-description">
                  Add one or more product lines. Rate is auto-filled from selected product.
                </Typography.Text>
              </div>
            </div>

            {productItems.map((item, index) => {
              const lineDraft = watchedItems?.[index] || {};
              const lineAmount = Number(lineDraft.quantity || 0) * Number(lineDraft.rate || 0);

              return (
                <div key={item.id} className="product-item-card">
                  <div className="product-item-header">
                    <Typography.Text strong className="product-number">
                      Product {index + 1}
                    </Typography.Text>

                    <div className="product-item-meta">
                      {lineAmount > 0 && (
                        <Typography.Text className="product-line-total">
                          Est. ₹{lineAmount.toLocaleString('en-IN', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography.Text>
                      )}

                      {productItems.length > 1 && (
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => handleRemoveProduct(index)}
                          className="remove-product-btn"
                          size="small"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>

                  <Row gutter={[16, 0]}>
                    <Col xs={24} sm={14}>
                      <Form.Item
                        label="Product"
                        name={['items', index, 'product_id']}
                        rules={[
                          {
                            required: true,
                            message: 'Please select a product!',
                          },
                        ]}
                      >
                        <Select
                          placeholder={
                            !watchedVendorId
                              ? 'Select a vendor first'
                              : vendorFilteredProducts.length === 0
                              ? 'No products linked to this vendor'
                              : 'Select product'
                          }
                          showSearch
                          size="large"
                          disabled={!watchedVendorId}
                          filterOption={(input, option) =>
                            String(option?.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={(value) => handleProductChange(value, index)}
                        >
                          {vendorFilteredProducts.length > 0 ? (
                            vendorFilteredProducts.map(product => (
                              <Option key={product.id} value={product.id}>
                                {product.product_name || product.name}
                              </Option>
                            ))
                          ) : (
                            <Option disabled>
                              {watchedVendorId ? 'No products linked to this vendor' : 'Select a vendor first'}
                            </Option>
                          )}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={12} sm={5}>
                      <Form.Item
                        label="Quantity"
                        name={['items', index, 'quantity']}
                        rules={[
                          {
                            required: true,
                            message: 'Required!',
                          },
                          {
                            type: 'number',
                            min: 1,
                            message: 'Min 1!',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="Qty"
                          min={1}
                          size="large"
                          style={{ width: '100%' }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={12} sm={5}>
                      <Form.Item
                        label="Rate (₹)"
                        name={['items', index, 'rate']}
                        rules={[
                          {
                            required: true,
                            message: 'Required!',
                          },
                        ]}
                      >
                        <InputNumber
                          placeholder="Auto-filled"
                          size="large"
                          style={{ width: '100%' }}
                          formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                          parser={value => (value ? value.replace(/\$\s?|(,*)/g, '') : '')}
                          prefix="₹"
                          disabled
                          readOnly
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              );
            })}

            <Button
              type="dashed"
              onClick={handleAddProduct}
              icon={<PlusOutlined />}
              block
              size="large"
              className="add-product-btn"
            >
              Add Product Line
            </Button>
          </div>

          <div className="form-actions-divider" />

          <Form.Item style={{ marginBottom: 0 }}>
            <div className="modal-footer-actions">
              <div className="modal-footer-summary">
                <strong>{orderDraftSummary.totalQuantity}</strong> total qty across{' '}
                <strong>{orderDraftSummary.itemCount}</strong> line(s)
                {watchedDeliveryDate ? ` • Delivery ${watchedDeliveryDate.format('DD MMM YYYY')}` : ''}
              </div>

              <div className="modal-footer-buttons">
                <Button
                  onClick={handleModalClose}
                  size="large"
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  icon={<ShoppingCartOutlined />}
                >
                  Create Order & Download PDF
                </Button>
              </div>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Pending Approval Modal (shown after PO creation) ─────── */}
      <Modal
        title={<Space><ClockCircleOutlined style={{ color: '#faad14' }} />Purchase Order Sent for Approval</Space>}
        open={!!pendingApprovalPO}
        onCancel={() => dispatch({ type: CLEAR_PO_PENDING_APPROVAL })}
        footer={<Button type="primary" onClick={() => dispatch({ type: CLEAR_PO_PENDING_APPROVAL })}>Got it</Button>}
      >
        {pendingApprovalPO && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              PO #{pendingApprovalPO.order_id} requires approval
            </div>
            <div style={{ color: '#595959', marginBottom: 16 }}>
              Total Amount: <strong style={{ color: '#1890ff' }}>
                ₹{Number(pendingApprovalPO.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </strong>
            </div>
            <Alert
              type="warning"
              showIcon
              message="This order exceeds the approval threshold and has been sent to the approvers. It will become active once approved."
            />
          </div>
        )}
      </Modal>

      {/* ── Reject Approval Modal ────────────────────────────────── */}
      <Modal
        title={<Space><CloseCircleOutlined style={{ color: '#ff4d4f' }} />Reject PO Approval</Space>}
        open={!!rejectModalPO}
        onCancel={() => { setRejectModalPO(null); setRejectReason(''); setRejectError(''); }}
        footer={
          <Space>
            <Button onClick={() => { setRejectModalPO(null); setRejectReason(''); setRejectError(''); }}>Cancel</Button>
            <Button danger icon={<CloseCircleOutlined />} loading={approvalActionLoading} onClick={handleRejectPO}>
              Reject
            </Button>
          </Space>
        }
        destroyOnClose
      >
        <Input.TextArea
          rows={3}
          placeholder="Rejection reason (required)…"
          value={rejectReason}
          onChange={(e) => { setRejectReason(e.target.value); setRejectError(''); }}
          status={rejectError ? 'error' : ''}
        />
        {rejectError && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{rejectError}</div>}
      </Modal>

      {/* ── Accept Shortfall Modal ───────────────────────────────── */}
      <Modal
        title={<Space><MinusCircleOutlined style={{ color: '#fa8c16' }} />Close &amp; Accept Shortfall</Space>}
        open={shortfallModal.open}
        onCancel={closeShortfallModal}
        footer={
          <Space>
            <Button onClick={closeShortfallModal}>Cancel</Button>
            <Button
              type="primary"
              style={{ background: '#fa8c16', borderColor: '#fa8c16' }}
              loading={shortfallLoading}
              onClick={handleAcceptShortfall}
            >
              Close Line &amp; Accept Shortfall
            </Button>
          </Space>
        }
        destroyOnClose
      >
        {shortfallModal.item && (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={`This will reduce the ordered quantity for "${shortfallModal.item.product_name}" from ${shortfallModal.item.quantity} to ${shortfallModal.item.delivered_quantity || 0} ${shortfallModal.item.unit || ''} to match what was actually delivered, and close this line. Use it only once you know no further delivery is coming.`}
            />
            <Input.TextArea
              rows={3}
              placeholder="Reason for accepting the shortfall (required)…"
              value={shortfallModal.reason}
              onChange={(e) => setShortfallModal(prev => ({ ...prev, reason: e.target.value, error: '' }))}
              status={shortfallModal.error ? 'error' : ''}
            />
            {shortfallModal.error && <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>{shortfallModal.error}</div>}
          </>
        )}
      </Modal>
    </div>
  );
};

export default PurchaseOrder;
